/**
 * StatComparisonRow — Stat Row v2 (UX-AUDIT-2026-07-31 R1).
 *
 * The ONE canonical stat-pair row, wireframe-INVARIANT anatomy:
 *
 *   [ home value | centred label | away value ]
 *   [ ◄──── home ────·──── away ────► ]  (optional centre-out bar)
 *
 * Home value pinned to the LEFT edge, away value pinned to the RIGHT
 * edge, the label owns the centre on a single ellipsised line. This is
 * the market-standard anatomy (Sofascore / FotMob / LMT): the values
 * form scannable vertical rails, sides are structural — never prose.
 * Axis, order and alignment are CORRECTNESS, not brand voice: the v1
 * anatomy dials (`--statrow-grid`, `--statrow-label-order`,
 * `--statrow-label-align`, `--statrow-value-align`) are RETIRED. Brands
 * keep every voice dial: label size/case/tracking, padding, column gap,
 * divider weight, bar thickness, trail opacity, leader-emphasis face.
 *
 * v2 changes vs the v4 "ledger row":
 *   - `renderType: 'bar'` (explicit, data-driven) replaces value-parsing
 *     as the ONLY way to get a bar. The bar is mirrored centre-out: the
 *     home half grows leftward from the centre, the away half rightward,
 *     each proportional to its share. 'numbers' / absent → no bar, no
 *     ghost spacing (the bar row only mounts when asked).
 *   - `tie` renders equal values at equal (regular) weight with a subtle
 *     "=" marker — "both teams equal" is data, never a neutral orphan.
 *   - Single-side mode: exactly one of `home`/`away` plus `subject`
 *     renders the value in THAT side's cell and a quiet "–" opposite —
 *     attribution is never omitted.
 *   - `lead` keeps its semantics unchanged: the leader carries the
 *     display face, the trailer goes quiet.
 *
 * Slot-based as before: `home`/`away` accept ReactNode (gauges, spans).
 * `labelTrailing` hosts the LayoutRenderer's inline info glyph.
 */

import type { ReactElement, ReactNode } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './StatComparisonRow.module.css';

/**
 * Wire shape (LLM-portable slice). Pairs stay required here — one-sided
 * stats reach the renderer through `LayoutBlock.stat` (structured
 * semantics), not through free-form props. `renderType` is the ONLY
 * decoration toggle: 'bar' is for share/ratio stats, everything else is
 * plain numbers. Composed values (nested primitives) are out of band.
 */
const dataShape = z.object({
  label: z.string(),
  home: z.union([z.string(), z.number()]),
  away: z.union([z.string(), z.number()]),
  /**
   * NEW (preferred for LLM emit): `lead` names the side that LEADS the
   * comparison — it keeps the display face, the other side goes quiet.
   * Maps the way an analyst thinks: "home wins this row → lead='home'".
   * Use 'both' when the comparison is genuinely tied (1,9 vs 1,9) — both
   * stay emphasised. OMIT when there is no winner and no clean tie
   * (qualitative data, missing values, etc.) — both render plain.
   */
  lead: z.enum(['home', 'away', 'both']).optional(),
  /**
   * LEGACY (back-compat only — DO NOT use from the LLM): `valueDim`
   * names the side to DIM. Originally for IhsGoals's "conceded per
   * game" row where BOTH numbers are bad ("dim both"). The semantic
   * confused the layout-planner LLM, which kept reading it as "name
   * the leader". `lead` is the new way.
   */
  valueDim: z.enum(['home', 'away', 'both']).optional(),
  /** 'bar' → mirrored centre-out share bar (ratio stats ONLY). */
  renderType: z.enum(['numbers', 'bar']).optional(),
  /** Adds the `stage-row` class so the row participates in IhsCard reveal. */
  animateOnInView: z.boolean().optional(),
});

export const META: PrimitiveMeta = {
  name: 'stat-comparison-row',
  description:
    'Stat row — home value left edge, centred label, away value right edge; optional centre-out share bar.',
  composable: true,
  dataShape,
  densities: ['standard'],
  breakpoints: { mobile: true, wide: true },
  animation: 'reveal',
};

interface StatComparisonRowProps {
  label: string;
  /** Optional inline icon rendered before the label inside the label cell. */
  labelIcon?: ReactNode;
  /** Optional trailing node at the end of the label cell — the
   *  LayoutRenderer mounts its inline (i) info glyph here. */
  labelTrailing?: ReactNode;
  /** Home value — LEFT edge. Optional only for single-side mode. */
  home?: ReactNode;
  /** Away value — RIGHT edge. Optional only for single-side mode. */
  away?: ReactNode;
  /** Single-side attribution: when exactly one of `home`/`away` is set,
   *  `subject` names whose the present value is. The value renders in
   *  that side's cell; the other side shows a quiet "–". */
  subject?: 'home' | 'away';
  /**
   * League average for this metric (like-for-like unit with home/away).
   * Renders the value-vs-league mini visualization under the row: each
   * side's bar against the league bar + a centred localized label
   * (caller passes `leagueAvgLabel` pre-localized — this primitive
   * stays i18n-free). Absent → row renders exactly as before.
   */
  leagueAvg?: number;
  /** Pre-localized label, e.g. "Ligasnitt 1,4". Required with leagueAvg. */
  leagueAvgLabel?: string;
  /** 'bar' → mirrored centre-out share bar under the text line. Absent
   *  or 'numbers' → no bar (explicit ask only — never value-parsing). */
  renderType?: 'numbers' | 'bar';
  /** Tied values: both render at regular weight + a subtle "=" marker.
   *  Wins over `lead` when both are passed. */
  tie?: boolean;
  /** PREFERRED: name the WINNING side; the other goes quiet. */
  lead?: 'home' | 'away' | 'both';
  /** LEGACY (back-compat): name the side to DIM. */
  valueDim?: 'home' | 'away' | 'both';
  /** When true, adds the `stage-row` class so the row participates in IhsCard's staged reveal. */
  animateOnInView?: boolean;
}

/**
 * Resolve the emphasis class for one side. `lead` wins if both are passed.
 *   lead='home'  → home leads, away goes quiet
 *   lead='away'  → away leads, home goes quiet
 *   lead='both'  → tied: neither dimmed, neither promoted
 *   valueDim='X' → dim 'X'  (or both if 'both')
 */
function emphasisClass(
  side: 'home' | 'away',
  lead: StatComparisonRowProps['lead'],
  valueDim: StatComparisonRowProps['valueDim'],
): string {
  if (lead) {
    if (lead === 'both') return '';
    return lead === side ? (styles.valueLead ?? '') : (styles.valueDim ?? '');
  }
  if (!valueDim) return '';
  if (valueDim === 'both') return styles.valueDim ?? '';
  if (valueDim === side) return styles.valueDim ?? '';
  return '';
}

/**
 * Parse a displayed value into a non-negative number for the share bar.
 * Handles "1,3" (comma decimal), "62%", plain numbers. Returns null when
 * the node isn't a plain parseable scalar — in which case no bar renders
 * even under `renderType='bar'` (we never fabricate a split).
 */
function toShareNumber(node: ReactNode): number | null {
  if (typeof node === 'number') {
    return Number.isFinite(node) && node >= 0 ? node : null;
  }
  if (typeof node !== 'string') return null;
  const cleaned = node.replace('%', '').replace(',', '.').trim();
  if (cleaned.length === 0) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function StatComparisonRow({
  label,
  labelIcon,
  labelTrailing,
  home,
  away,
  subject,
  leagueAvg,
  leagueAvgLabel,
  renderType,
  tie = false,
  lead,
  valueDim,
  animateOnInView = false,
}: StatComparisonRowProps): ReactElement {
  const rowClass = animateOnInView ? `${styles.row} stage-row` : styles.row;

  // ── Single-side mode ──────────────────────────────────────────────────
  // Exactly one value present + `subject` naming its side: the value goes
  // in the subject's cell, the opposite cell gets a quiet "–". Attribution
  // is a wireframe invariant — a lone number never floats unanchored.
  const quiet = <span className={styles.valueDim}>–</span>;
  let homeNode: ReactNode = home ?? quiet;
  let awayNode: ReactNode = away ?? quiet;
  const singleSide =
    subject !== undefined && (home === undefined) !== (away === undefined);
  if (singleSide) {
    const present = home ?? away;
    homeNode = subject === 'home' ? present : quiet;
    awayNode = subject === 'away' ? present : quiet;
  }

  // ── Centre-out bar (explicit ask only) ────────────────────────────────
  const homeNum = renderType === 'bar' ? toShareNumber(home) : null;
  const awayNum = renderType === 'bar' ? toShareNumber(away) : null;
  const total = homeNum !== null && awayNum !== null ? homeNum + awayNum : 0;
  const showShareBar = renderType === 'bar' && total > 0;
  const homePct = showShareBar ? ((homeNum as number) / total) * 100 : 0;

  // ── League-average mini visualization (value-vs-league bars) ──────────
  // Same visual language as the gem-atom hero: side bar over league bar,
  // widths on a shared scale. Only when the caller ships a real average
  // AND at least one side parses numerically — never fabricated.
  const homeAvgNum = toShareNumber(home);
  const awayAvgNum = toShareNumber(away);
  const showAvgLine =
    leagueAvg !== undefined
    && Number.isFinite(leagueAvg)
    && leagueAvg > 0
    && leagueAvgLabel !== undefined
    && (homeAvgNum !== null || awayAvgNum !== null);
  const avgScale = showAvgLine
    ? Math.max(homeAvgNum ?? 0, awayAvgNum ?? 0, leagueAvg)
    : 1;
  const avgWidth = (n: number): number =>
    Math.max(8, Math.round((n / avgScale) * 100));

  const homeClass = tie ? (styles.valueTie ?? '') : emphasisClass('home', lead, valueDim);
  const awayClass = tie ? (styles.valueTie ?? '') : emphasisClass('away', lead, valueDim);

  return (
    <div className={rowClass}>
      <span className={`${styles.value} ${styles.valueHome} ${homeClass}`}>{homeNode}</span>
      <span className={styles.labelCell}>
        {labelIcon}
        <span className={styles.labelText} title={label}>
          {label}
        </span>
        {labelTrailing}
      </span>
      <span className={`${styles.value} ${styles.valueAway} ${awayClass}`}>{awayNode}</span>
      {showAvgLine ? (
        <span className={styles.avgLine} aria-hidden="true">
          <span className={styles.avgSide}>
            {homeAvgNum !== null ? (
              <span className={styles.avgBars}>
                <span
                  className={styles.avgBarValue}
                  style={{ width: `${avgWidth(homeAvgNum)}%` }}
                />
                <span
                  className={styles.avgBarBase}
                  style={{ width: `${avgWidth(leagueAvg)}%` }}
                />
              </span>
            ) : null}
          </span>
          <span className={styles.avgLabel}>{leagueAvgLabel}</span>
          <span className={`${styles.avgSide} ${styles.avgSideAway}`}>
            {awayAvgNum !== null ? (
              <span className={`${styles.avgBars} ${styles.avgBarsAway}`}>
                <span
                  className={styles.avgBarValue}
                  style={{ width: `${avgWidth(awayAvgNum)}%` }}
                />
                <span
                  className={styles.avgBarBase}
                  style={{ width: `${avgWidth(leagueAvg)}%` }}
                />
              </span>
            ) : null}
          </span>
        </span>
      ) : null}
      {showShareBar ? (
        <span className={styles.shareBar} aria-hidden="true">
          <span className={`${styles.shareHalf} ${styles.shareHalfHome}`}>
            <span
              className={`${styles.shareFillHome} ${!tie && lead === 'away' ? (styles.shareTrail ?? '') : ''}`}
              style={{ width: `${homePct}%` }}
            />
          </span>
          <span className={`${styles.shareHalf} ${styles.shareHalfAway}`}>
            <span
              className={`${styles.shareFillAway} ${!tie && lead === 'home' ? (styles.shareTrail ?? '') : ''}`}
              style={{ width: `${100 - homePct}%` }}
            />
          </span>
        </span>
      ) : null}
    </div>
  );
}

export default StatComparisonRow;
